// Liste éditable des véhicules d'un contact antenne.
// - Mode EDIT (contactId fourni) : appels API directs (CRUD via /api/vehicules/*).
// - Mode CREATE : le composant garde les véhicules en mémoire ; le parent peut
//   les récupérer après création du contact via la ref (méthode flushPending).
//
// UI : table des véhicules avec actions inline (Modifier, Supprimer), puis bouton
// "+ Ajouter un véhicule" qui ouvre un formulaire inline.

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Pencil, Trash2, Plus, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getVehiculesForContact,
  createVehicule,
  updateVehicule,
  deleteVehicule,
} from '../services/api';
import { fetchAllMakes, fetchModelsForMake } from '../lib/carquery';
import { usePerm } from '../lib/perms';

const emptyVehicule = () => ({
  marque: '', modele: '', annee: '', version: '',
  couleur: '', immatriculation: '', commentaire: '',
  nb_bornes_transport: '',
});

// ─────────────────────────────────────────────────────────────────────
// Combobox simple : input typeable + dropdown options filtrées.
// Permet la saisie manuelle même si la valeur n'est pas dans la liste.
// ─────────────────────────────────────────────────────────────────────
function Combobox({ value, onChange, options, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const filtered = !value
    ? options
    : options.filter((o) => o.toLowerCase().includes(value.toLowerCase()));

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlighted(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
          } else if (e.key === 'Enter' && highlighted >= 0) {
            e.preventDefault();
            onChange(filtered[highlighted]);
            setOpen(false);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          maxHeight: 220, overflowY: 'auto', background: 'white',
          border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 10px 24px rgba(15,23,42,0.12)',
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 10px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              {options.length === 0
                ? (disabled ? 'Sélectionnez d\'abord une marque' : 'Aucune option (saisie libre possible)')
                : 'Aucun résultat. Vous pouvez saisir librement.'}
            </div>
          ) : filtered.slice(0, 100).map((opt, i) => (
            <div
              key={opt}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt); setOpen(false); }}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                padding: '7px 10px', fontSize: 13, cursor: 'pointer',
                background: i === highlighted ? '#eef2ff' : 'transparent',
                color: '#0f172a',
              }}
            >
              {opt}
            </div>
          ))}
          {filtered.length > 100 && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: '#94a3b8', textAlign: 'center', borderTop: '1px solid #f1f5f9' }}>
              + {filtered.length - 100} autres (affinez la recherche)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Formulaire inline d'édition / ajout d'un véhicule.
// ─────────────────────────────────────────────────────────────────────
function VehiculeForm({ initial, onSave, onCancel, saving }) {
  const [marque, setMarque] = useState(initial?.marque || '');
  const [modele, setModele] = useState(initial?.modele || '');
  // « Année » et « Version » ne sont plus affichés (demande client), mais les
  // valeurs existantes sont conservées et renvoyées telles quelles : la colonne
  // reste en base, une modification de fiche ne doit pas l'effacer.
  const [annee] = useState(initial?.annee || '');
  const [version] = useState(initial?.version || '');
  const [couleur, setCouleur] = useState(initial?.couleur || '');
  const [immatriculation, setImmatriculation] = useState(initial?.immatriculation || '');
  const [commentaire, setCommentaire] = useState(initial?.commentaire || '');
  const [nbBornesTransport, setNbBornesTransport] = useState(
    initial?.nb_bornes_transport !== null && initial?.nb_bornes_transport !== undefined
      ? String(initial.nb_bornes_transport) : ''
  );

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingMakes(true);
    fetchAllMakes()
      .then((data) => { if (!cancelled) setMakes(data); })
      .catch(() => { if (!cancelled) setMakes([]); })
      .finally(() => { if (!cancelled) setLoadingMakes(false); });
    return () => { cancelled = true; };
  }, []);

  // Recharge la liste de modèles quand la marque change. Plus de filtrage par
  // année : elle n'est plus saisissable, filtrer sur une valeur invisible
  // donnerait une liste écourtée sans explication.
  useEffect(() => {
    if (!marque || marque.length < 2) { setModels([]); return undefined; }
    let cancelled = false;
    setLoadingModels(true);
    fetchModelsForMake(marque)
      .then((data) => { if (!cancelled) setModels(data); })
      .catch(() => { if (!cancelled) setModels([]); })
      .finally(() => { if (!cancelled) setLoadingModels(false); });
    return () => { cancelled = true; };
  }, [marque]);

  const submit = () => {
    const nbBornes = nbBornesTransport.trim();
    onSave({
      marque: marque.trim() || null,
      modele: modele.trim() || null,
      annee: annee ? parseInt(annee, 10) : null,
      version: version.trim() || null,
      couleur: couleur.trim() || null,
      immatriculation: immatriculation.trim() || null,
      commentaire: commentaire.trim() || null,
      nb_bornes_transport: nbBornes !== '' ? parseInt(nbBornes, 10) : null,
    });
  };

  return (
    <div style={{
      border: '1px solid #c7d2fe', background: '#fafbff',
      borderRadius: 10, padding: 14, marginBottom: 10,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>
            Marque {loadingMakes && <Loader2 size={11} className="spin" style={{ display: 'inline', verticalAlign: 'middle' }} />}
          </label>
          <Combobox
            value={marque}
            onChange={(v) => { setMarque(v); setModele(''); }}
            options={makes}
            placeholder="Toyota, Renault, BMW… (saisie libre possible)"
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>
            Modèle {loadingModels && <Loader2 size={11} className="spin" style={{ display: 'inline', verticalAlign: 'middle' }} />}
          </label>
          <Combobox
            value={modele}
            onChange={setModele}
            options={models}
            placeholder={marque ? 'Choisir ou saisir le modèle' : 'Sélectionnez d\'abord une marque'}
            disabled={!marque}
          />
        </div>
      </div>

      {/* « Année » et « Version » retirés de l'affichage (demande client) —
          les colonnes restent en base et les valeurs sont préservées. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>Couleur</label>
          <input
            type="text"
            value={couleur}
            onChange={(e) => setCouleur(e.target.value)}
            placeholder="Bleu marine"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>Immatriculation</label>
          <input
            type="text"
            value={immatriculation}
            onChange={(e) => setImmatriculation(e.target.value.toUpperCase())}
            placeholder="AB-123-CD"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', textTransform: 'uppercase' }}
          />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>
          Nombre de borne(s) transportable par trajet
        </label>
        <input
          type="number"
          min="0"
          step="1"
          value={nbBornesTransport}
          onChange={(e) => setNbBornesTransport(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="Ex : 2"
          style={{ width: 140, padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>Commentaire</label>
        <textarea
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          placeholder="Notes libres (état, places de parking, etc.)"
          rows={2}
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', color: '#475569', fontSize: 12, cursor: 'pointer' }}
        >
          <X size={13} /> Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 6, border: 'none', background: '#4f46e5', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          {saving ? <Loader2 size={13} className="spin" /> : <Check size={13} />}
          Valider
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Composant principal — exposé en forwardRef pour permettre au parent
// (ContactForm en mode CREATE) de récupérer la liste pour la flusher après
// création du contact.
// ─────────────────────────────────────────────────────────────────────
const VehiculesList = forwardRef(({ contactId }, ref) => {
  const isEdit = Boolean(contactId);
  const canCreate = usePerm('vehicules.create');
  const canEdit = usePerm('vehicules.edit');
  const canDelete = usePerm('vehicules.delete');

  const [vehicules, setVehicules] = useState([]); // mode EDIT : id BDD ; mode CREATE : id local (Date.now())
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // En mode CREATE, on stocke les véhicules pending pour les flusher à la création
  const pendingRef = useRef([]);
  useImperativeHandle(ref, () => ({
    // Appelé par le parent après création du contact : envoie en série tous les véhicules.
    flushPending: async (newContactId) => {
      if (isEdit) return;
      for (const v of pendingRef.current) {
        try {
          await createVehicule(newContactId, v);
        } catch (e) {
          console.error('Vehicule flush failed', e);
        }
      }
      pendingRef.current = [];
    },
  }));

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setLoading(true);
    getVehiculesForContact(contactId)
      .then((data) => { if (!cancelled) setVehicules(data); })
      .catch(() => { if (!cancelled) toast.error('Erreur de chargement des véhicules'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contactId, isEdit]);

  const handleSaveNew = async (payload) => {
    if (isEdit) {
      setSaving(true);
      try {
        const created = await createVehicule(contactId, payload);
        setVehicules((prev) => [...prev, created]);
        setAdding(false);
        toast.success('Véhicule ajouté');
      } catch (e) {
        toast.error(e.response?.data?.error || 'Erreur lors de l\'ajout');
      } finally {
        setSaving(false);
      }
    } else {
      // Mode CREATE : pas encore d'API, on ajoute en mémoire avec un id local
      const localId = `tmp-${Date.now()}`;
      const v = { ...payload, id: localId };
      pendingRef.current = [...pendingRef.current, payload];
      setVehicules((prev) => [...prev, v]);
      setAdding(false);
    }
  };

  const handleSaveEdit = async (id, payload) => {
    if (isEdit && !String(id).startsWith('tmp-')) {
      setSaving(true);
      try {
        const updated = await updateVehicule(id, payload);
        setVehicules((prev) => prev.map((v) => (v.id === id ? updated : v)));
        setEditingId(null);
        toast.success('Véhicule modifié');
      } catch (e) {
        toast.error(e.response?.data?.error || 'Erreur lors de la modification');
      } finally {
        setSaving(false);
      }
    } else {
      // Mode CREATE : mise à jour locale
      setVehicules((prev) => prev.map((v) => (v.id === id ? { ...v, ...payload } : v)));
      // Mettre à jour pendingRef en réalignant sur l'ordre des vehicules
      pendingRef.current = pendingRef.current.map((p, idx) => {
        const cur = vehicules[idx];
        if (cur?.id === id) return payload;
        return p;
      });
      setEditingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    if (isEdit && !String(id).startsWith('tmp-')) {
      try {
        await deleteVehicule(id);
        setVehicules((prev) => prev.filter((v) => v.id !== id));
        toast.success('Véhicule supprimé');
      } catch (e) {
        toast.error('Erreur lors de la suppression');
      }
    } else {
      const idx = vehicules.findIndex((v) => v.id === id);
      setVehicules((prev) => prev.filter((v) => v.id !== id));
      if (idx >= 0) {
        pendingRef.current = pendingRef.current.filter((_, i) => i !== idx);
      }
    }
  };

  return (
    <div>
      {loading && (
        <div style={{ padding: 12, color: '#64748b', fontSize: 13 }}>
          <Loader2 size={14} className="spin" style={{ display: 'inline', verticalAlign: 'middle' }} /> Chargement…
        </div>
      )}

      {!loading && vehicules.map((v) => (
        editingId === v.id ? (
          <VehiculeForm
            key={v.id}
            initial={v}
            saving={saving}
            onCancel={() => setEditingId(null)}
            onSave={(payload) => handleSaveEdit(v.id, payload)}
          />
        ) : (
          <div
            key={v.id}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 12px', marginBottom: 6,
              background: 'white', border: '1px solid #e2e8f0', borderRadius: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                {[v.marque, v.modele].filter(Boolean).join(' ') || '(véhicule sans marque)'}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                {[
                  v.couleur,
                  v.immatriculation,
                  v.nb_bornes_transport !== null && v.nb_bornes_transport !== undefined
                    ? `${v.nb_bornes_transport} borne${v.nb_bornes_transport > 1 ? 's' : ''} / trajet`
                    : null,
                ].filter(Boolean).join(' · ') || '—'}
                {v.commentaire && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>« {v.commentaire} »</span>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {(canEdit || !isEdit) && (
                <button
                  type="button"
                  onClick={() => setEditingId(v.id)}
                  title="Modifier"
                  data-perm="antennes:vehicules.edit"
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', color: '#475569', cursor: 'pointer' }}
                >
                  <Pencil size={13} />
                </button>
              )}
              {(canDelete || !isEdit) && (
                <button
                  type="button"
                  onClick={() => handleDelete(v.id)}
                  title="Supprimer"
                  data-perm="antennes:vehicules.delete"
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        )
      ))}

      {!loading && adding && (
        <VehiculeForm
          initial={emptyVehicule()}
          saving={saving}
          onCancel={() => setAdding(false)}
          onSave={handleSaveNew}
        />
      )}

      {!loading && !adding && (canCreate || !isEdit) && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-perm="antennes:vehicules.create"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            width: '100%', padding: '10px 14px', marginTop: 4,
            borderRadius: 8, border: '1px dashed #c7d2fe',
            background: '#eef2ff', color: '#4338ca',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Plus size={14} /> Ajouter un véhicule
        </button>
      )}
    </div>
  );
});

export default VehiculesList;
